import React, { useState } from 'react'
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material'
import {
  Close as CloseIcon,
  Send as SendIcon,
} from '@mui/icons-material'

function ContactDialog({ open, onClose, darkMode }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  })
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
    setError('')
    setSuccess(false)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setSuccess(false)

    // Validation
    if (!formData.name || !formData.email || !formData.message) {
      setError('Please fill in all required fields')
      setLoading(false)
      return
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address')
      setLoading(false)
      return
    }

    try {
      // Option 1: Using EmailJS (Recommended)
      const response = await fetch('https://api.emailjs.com/api/v1.0/email/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          service_id: 'service_h0z2h8y', 
          template_id: 'template_2b0kh6v',
          user_id: '50K-3wpaSETkvndFW',
          template_params: {
            from_name: formData.name,
            from_email: formData.email,
            subject: formData.subject || 'Contact Form Submission',
            message: formData.message,
            to_email: 'divyanshece242@gmail.com',
            cc_email: 'tushar20231102@iitgoa.ac.in',
          },
        }),
      })

      if (response.ok) {
        setSuccess(true)
        setFormData({ name: '', email: '', subject: '', message: '' })
        setTimeout(() => {
          onClose()
          setSuccess(false)
        }, 2000)
      } else {
        throw new Error('Failed to send message')
      }
    } catch (err) {
      console.error('Email error:', err)
      
      // Fallback: Open mailto link
      const mailtoLink = `mailto:divyanshece242@gmail.com?subject=${encodeURIComponent(
        formData.subject || 'Contact from SupplyNet Web'
      )}&body=${encodeURIComponent(
        `Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`
      )}`
      
      window.location.href = mailtoLink
      
      setSuccess(true)
      setTimeout(() => {
        onClose()
        setSuccess(false)
      }, 2000)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: darkMode ? '#1e293b' : '#fff',
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
        },
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 16,
          top: 16,
          color: darkMode ? '#94a3b8' : '#64748b',
          zIndex: 1,
          '&:hover': {
            bgcolor: darkMode ? '#334155' : '#f1f5f9',
            transform: 'rotate(90deg)',
          },
          transition: 'all 0.3s ease',
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography
            variant="h4"
            fontWeight={700}
            sx={{
              mb: 1,
              color: darkMode ? '#fff' : '#0f172a',
            }}
          >
            Get in Touch
          </Typography>
          <Typography
            variant="body2"
            color={darkMode ? '#94a3b8' : '#64748b'}
          >
            Have questions? We'd love to hear from you
          </Typography>
        </Box>

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Message sent successfully! We'll get back to you soon.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Your Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              fullWidth
              required
              disabled={loading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: darkMode ? '#0f172a' : '#f8fafc',
                },
              }}
            />

            <TextField
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              fullWidth
              required
              disabled={loading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: darkMode ? '#0f172a' : '#f8fafc',
                },
              }}
            />

            <TextField
              label="Subject (Optional)"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              fullWidth
              disabled={loading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: darkMode ? '#0f172a' : '#f8fafc',
                },
              }}
            />

            <TextField
              label="Message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              fullWidth
              required
              multiline
              rows={4}
              disabled={loading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: darkMode ? '#0f172a' : '#f8fafc',
                },
              }}
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
              sx={{
                py: 1.5,
                fontWeight: 600,
                textTransform: 'none',
                background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                },
              }}
            >
              {loading ? 'Sending...' : 'Send Message'}
            </Button>
          </Box>
        </form>

        <Typography
          variant="caption"
          color={darkMode ? '#64748b' : '#94a3b8'}
          sx={{ display: 'block', textAlign: 'center', mt: 2 }}
        >
          Or email directly: divyanshece242@gmail.com
        </Typography>
      </DialogContent>
    </Dialog>
  )
}

export default ContactDialog
